import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { AlertCircle, Check, Copy, Loader2 } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@shared/components/ui/input-otp';
import { keycloakService, type OtpEnrollment } from '@features/auth/services/keycloak.service';

interface OtpEnrollmentFormProps {
  /** Email of the account enrolling — used to label the authenticator entry. */
  email: string;
  /** Called after the enrollment code is verified successfully. */
  onEnrolled: () => void;
  /** Optional skip handler (e.g. "set up later"). Omit to make enrollment mandatory. */
  onSkip?: () => void;
}

const OTP_LENGTH = 6;

/**
 * TOTP enrollment: fetches an otpauth URI from the auth seam, renders it as a
 * QR code for scanning, and confirms setup by verifying the first generated code.
 */
export function OtpEnrollmentForm({ email, onEnrolled, onSkip }: OtpEnrollmentFormProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [enrollment, setEnrollment] = useState<OtpEnrollment | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Fetch the enrollment secret once on mount.
  useEffect(() => {
    let active = true;
    keycloakService
      .startOtpEnrollment(email)
      .then((result) => {
        if (active) setEnrollment(result);
      })
      .catch((err) => {
        if (active) {
          setLoadError(err instanceof Error ? err.message : 'Unable to start setup');
        }
      });
    return () => {
      active = false;
    };
  }, [email]);

  // Render the QR code onto the canvas whenever we receive an otpauth URI.
  useEffect(() => {
    if (enrollment && canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, enrollment.otpauthUri, { width: 180, margin: 1 }, (err) => {
        if (err) setLoadError('Unable to render the QR code.');
      });
    }
  }, [enrollment]);

  const handleCopy = async () => {
    if (!enrollment) return;
    try {
      await navigator.clipboard.writeText(enrollment.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Unable to copy the secret');
    }
  };

  const submit = async (value: string) => {
    if (value.length !== OTP_LENGTH || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await keycloakService.confirmOtpEnrollment(email, value);
      toast.success('Authenticator app connected');
      onEnrolled();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="mb-6 text-center">
        <h2 className="mb-2 text-2xl font-semibold text-foreground">Set up two-step verification</h2>
        <p className="text-pretty text-sm text-muted-foreground">
          Scan the QR code with an authenticator app (Google Authenticator, 1Password, Authy), then
          enter the 6-digit code to confirm.
        </p>
      </div>

      {loadError && (
        <Alert variant="destructive" icon={AlertCircle} className="mb-6">
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      )}

      <div className="mb-6 flex flex-col items-center gap-4">
        <div className="flex size-[196px] items-center justify-center rounded-xl border bg-card p-2">
          {enrollment ? (
            <canvas ref={canvasRef} aria-label="Authenticator QR code" />
          ) : (
            !loadError && <Loader2 className="size-6 animate-spin text-muted-foreground" />
          )}
        </div>

        {enrollment && (
          <div className="w-full">
            <p className="mb-1 text-center text-xs text-muted-foreground">
              Can&apos;t scan? Enter this key manually
            </p>
            <button
              type="button"
              onClick={handleCopy}
              className="flex w-full items-center justify-center gap-2 rounded-md border bg-muted/40 px-3 py-2 font-mono text-sm text-foreground transition-colors hover:bg-muted"
            >
              <span className="tracking-widest">{enrollment.secret}</span>
              {copied ? (
                <Check className="size-4 text-primary" />
              ) : (
                <Copy className="size-4 text-muted-foreground" />
              )}
            </button>
          </div>
        )}
      </div>

      {error && (
        <Alert variant="destructive" icon={AlertCircle} className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit(code);
        }}
        className="flex flex-col items-center gap-6"
      >
        <InputOTP
          maxLength={OTP_LENGTH}
          value={code}
          onChange={(value) => {
            setCode(value);
            if (value.length === OTP_LENGTH) void submit(value);
          }}
          disabled={submitting || !enrollment}
          aria-label="Verification code"
        >
          <InputOTPGroup>
            {Array.from({ length: OTP_LENGTH }).map((_, i) => (
              <InputOTPSlot key={i} index={i} />
            ))}
          </InputOTPGroup>
        </InputOTP>

        <Button
          type="submit"
          className="h-11 w-full font-medium"
          disabled={submitting || code.length !== OTP_LENGTH}
        >
          {submitting ? 'Verifying...' : 'Confirm and continue'}
        </Button>
      </form>

      {onSkip && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={onSkip}
            className="text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            Set up later
          </button>
        </div>
      )}
    </div>
  );
}
